import { deepmerge } from "@fastify/deepmerge";
import {
    defaultDashboardConfig,
    type DashboardSettings,
    type DashboardTile as DashboardTileType,
} from "@pentacode/core";
import "@pentacode/ui/components";
import type { IconName } from "@pentacode/ui/components";
import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import "../../../../elements/date-input";
import "../../../../elements/issue";
import "../../../../elements/scroller";
import "../../../../elements/sortable-list";
import { app } from "../../../../init";
import { shared } from "../../../../styles";

const mergeDashboardSettings = deepmerge();

export type TileConfig = {
    type: DashboardTileType;
    label: string;
    subLabel?: string;
    icon: IconName;
    size:
        | number
        | {
              inline: number;
              block: number;
          };
};

@customElement("ptc-dashboard-tile")
export class DashboardTile extends LitElement {
    @property({ type: Boolean })
    loading = false;

    @property({ type: String })
    type: TileConfig["type"];

    @property({ type: String })
    label: TileConfig["label"];

    @property({ type: String })
    subLabel?: TileConfig["subLabel"];

    @property({ attribute: false })
    size: TileConfig["size"];

    @property({ type: String })
    icon: TileConfig["icon"];

    @property({ type: Boolean, attribute: "settings-open", reflect: true })
    settingsOpen = false;

    @property({ attribute: false })
    settingsForm?: HTMLFormElement;

    @property({ type: Boolean })
    allowResize = true;

    @property({ type: String })
    tileType: TileConfig["type"];

    @state()
    private _previewSize: { inline: number; block: number } | null = null;

    @state()
    private _selectedSize: { inline: number; block: number } = { inline: 1, block: 1 };

    @state()
    private clickHandlerTimers: number[] = [];

    get hasSettings() {
        return Boolean(this.settingsForm) || this.allowResize;
    }

    static styles = [
        shared,
        css`
            /* Host element styles */
            :host {
                --tile-size: 1;

                --tile-border-color: hsl(0 0% 75%);

                /* Layout */
                display: grid;
                grid-template-rows: min-content auto;
                grid-column: span var(--tile-size-inline, var(--tile-size));
                grid-row: span var(--tile-size-block, var(--tile-size));

                /* Sizing */
                min-height: var(--tile-base-size);
                height: calc(
                    var(--tile-size-block, var(--tile-size)) *
                        (var(--tile-base-size) + var(--tile-gap)) - var(--tile-gap)
                );
                overflow: hidden;

                /* Appearance */
                border: 1.5px solid var(--tile-border-color);
                border-radius: var(--border-radius);
                background:
                    linear-gradient(var(--color-bg-light), var(--color-bg-light)) padding-box,
                    linear-gradient(125deg, var(--tile-border-color), var(--tile-icon-color, var(--blue))) border-box;

                transition: border-color 0.2s ease-in-out;
            }

            :host(:empty) {
                opacity: 0.5;
            }

            :host(:empty) [part="header"] {
                border-block-end: 1.5px solid transparent;
            }

            /* Header section */
            [part="header"] {
                display: flex;
                align-items: center;
                justify-content: space-between;
                height: min-content;
                padding: 0.5rem 1rem;

                font-size: 1rem;
                font-weight: 600;

                background: var(--color-bg-light);
                border-block-end: 1.5px solid var(--tile-border-color);
                user-select: none;
                z-index: 3;
            }

            [part="header"][hidden] ~ [part="content"] {
                grid-row: span 2;
            }

            /* Content section */
            [part="content"] {
                display: grid;
                overflow: auto;
                transition: 0.2s ease-in-out;
                transition-property: filter, transform;
            }

            [part="content"],
            [part="settings"] {
                grid-area: 2 / 1;
            }

            ::slotted(*) {
                box-sizing: border-box;
                padding: var(--tile-content-padding, 1rem);
            }

            /* Settings panel */

            /* Icon styles */
            [part="spinner"],
            [part="icon"] {
                width: 1lh;
                height: 1lh;
                transition: opacity 0.2s ease-in-out;

                @starting-style {
                    opacity: 0;
                }
            }

            [part="icon"] {
                display: grid;
                place-content: center;
                line-height: inherit;
                color: var(--tile-icon-color);
                font-weight: normal;

                & > * {
                    grid-area: 1 / 1;
                    place-self: center;
                    transition: 0.2s ease-in-out;
                    transition-property: transform, opacity, background-color;
                }
            }

            [part="close-settings-icon"] {
                opacity: 0;
                transform: scale(0);
            }

            [part="settings-toggle"] {
                display: grid;
                place-content: center;
                padding: 0;
                cursor: pointer;
                opacity: 1;
                transform: rotate(-0.5turn);
                border-radius: var(--border-radius);
                font-weight: normal;

                & > * {
                    grid-area: 1 / 1;
                    place-self: center;
                }

                ptc-ui-icon {
                    transition: inherit;
                }
            }

            [part="open-settings-icon"],
            [part="close-settings-icon"] {
                opacity: 0;
                transform: scale(0.5) rotate(0turn);
                transform-origin: center;
            }

            [part="settings"] {
                pointer-events: none;
                transform: scale(1.2);
                opacity: 0;
                z-index: 2;
                filter: blur(5px);
                transition: 0.2s ease-in-out;
                transition-property: transform, opacity, filter;
                display: grid;
                grid-template-rows: auto min-content;
                overflow: auto;
                padding: 0.5rem 1rem;

                background: color-mix(in srgb, var(--color-bg-light) 90%, transparent);

                ::slotted(form) {
                    display: flex;
                    flex-direction: column;
                    justify-content: start;
                    align-items: start;

                    padding-block-end: 1rem !important;
                }

                footer {
                    font-size: var(--font-size-small);
                    grid-row: 2;
                    position: sticky;
                    bottom: 0;
                    display: flex;
                    justify-content: end;
                    gap: 1rem;
                    pointer-events: none;

                    & > * {
                        pointer-events: all;
                    }
                }
            }

            /* Interactive states */

            :host([settings-open]) {
                border-color: transparent;
                position: relative;
                z-index: 10;

                [part="tile-icon"] {
                    opacity: 0;
                    transform: scale(0.5);
                }

                [part="close-settings-icon"] {
                    opacity: 1;
                    transform: scale(1);
                }

                [part="open-settings-icon"] {
                    opacity: 0;
                    transform: scale(0.5) rotate(1.25turn);
                }

                [part="content"] {
                    filter: blur(5px);
                    transform: scale(0.8);
                }

                [part="settings"] {
                    transform: none;
                    filter: none;
                    opacity: 1;
                    pointer-events: all;
                }
            }

            [part="header"][data-has-settings="true"]:hover,
            [part="header"][data-has-settings="true"]:has(:focus-visible) {
                [part="tile-icon"] {
                    opacity: 0;
                    transform: scale(0.5);
                }

                [part="open-settings-icon"] {
                    opacity: 1;
                    transform: rotate(0.5turn);
                }
            }

            :host([settings-open]) [part="header"][data-has-settings="true"]:hover,
            :host([settings-open]) [part="header"][data-has-settings="true"]:has(:focus-visible) {
                [part="open-settings-icon"] {
                    opacity: 0;
                    transform: scale(0.5) rotate(1.25turn);
                }

                [part="close-settings-icon"] {
                    opacity: 1;
                    transform: scale(1);
                }
            }

            [part="settings-toggle"]:hover,
            [part="settings-toggle"]:has(:focus-visible) {
                background: var(--shade-1);

                ptc-ui-icon {
                    transform: rotate(0.25turn);
                }
            }

            .tile-size-grid {
                display: grid;
                grid-template-columns: repeat(4, 1rem);
                grid-template-rows: repeat(4, 1rem);
                gap: 0.25rem;
                margin-block: 0.5rem;
                user-select: none;
            }

            .tile-size-cell {
                border: 1px solid var(--tile-border-color);
                background: var(--color-bg-light);
                border-radius: 0.25em;
                transition: all 0.2s ease;
                cursor: pointer;
            }

            .tile-size-cell.selected {
                background: var(--blue);
                border-color: var(--blue);
            }

            .tile-size-cell.preview {
                background: var(--blue-bg);
                border-color: var(--blue);
            }

            .tile-size-label {
                margin-top: 0.5rem;
                font-size: 0.875rem;
                color: var(--color-text-secondary);
                text-align: center;
            }
        `,
    ];

    private _resetSettings(ev: MouseEvent) {
        ev.preventDefault();

        // Get default size based on tile type
        const defaultSize = defaultDashboardConfig.tiles[this.tileType]?.size || { inline: 1, block: 1 };

        // Reset the selected size to default
        this._selectedSize = { ...defaultSize };

        // Update the tile size
        this.size = { ...defaultSize };

        // Dispatch event to notify parent
        this._updateTileSizeAppSettings({ ...defaultSize });

        // Dispatch custom event to notify parent of size change
        this.dispatchEvent(
            new CustomEvent("tile-size-change", {
                detail: { size: { ...defaultSize } },
                bubbles: true,
                composed: true,
            })
        );

        this.settingsForm?.reset();
        this.settingsForm?.requestSubmit();
        this.settingsOpen = false;
    }

    private _updateTileSizeAppSettings(size: { inline: number; block: number }) {
        if (!this.tileType) {
            console.error("Tile type is not set, cannot update tile size");
            return;
        }

        app.updateSettings({
            dashboard: mergeDashboardSettings(app.settings.dashboard, {
                tiles: {
                    [this.tileType]: {
                        size: size, // @TODO: use schema validation to ensure the size is valid
                    },
                },
            }) as DashboardSettings, // @TODO: typecasting is a hack, but it works for now
        }).catch((error) => {
            console.error("Failed to save tile size:", error);
        });
    }

    toggleSettings() {
        if (this.settingsOpen) {
            this.settingsForm?.requestSubmit();
        } else {
            queueMicrotask(() => {
                const input = this.settingsForm?.querySelector("input");
                if (input) {
                    input.focus();

                    if (["text", "number"].includes(input.type)) input.select();
                }
            });
        }

        this.settingsOpen = !this.settingsOpen;
    }

    private _clickOutsideHandler = (event: MouseEvent) => {
        if (!this.settingsOpen) return;

        // Use composedPath to get elements through shadow DOM boundary
        const path = event.composedPath();

        // Check if any element in the path is this dashboard tile
        const clickedInsideTile = path.includes(this);

        if (!clickedInsideTile) {
            this.settingsForm?.requestSubmit();
            this.settingsOpen = false;
        }
    };

    connectedCallback() {
        super.connectedCallback();
        // Initialize selected size from current size
        if (typeof this.size === "object") {
            this._selectedSize = { ...this.size };
        }
    }

    private _handleGridHover = (row: number, col: number) => {
        this._previewSize = { inline: col, block: row };
    };

    private _handleGridLeave = () => {
        this._previewSize = null;
    };

    _handleGridClick(row: number, col: number) {
        this._selectedSize = { inline: col, block: row };
        // Immediately update the tile size
        this.size = { inline: col, block: row };
        // Dispatch event to notify parent
        this._updateTileSizeAppSettings({ inline: col, block: row });

        // Dispatch custom event to notify parent of size change
        this.dispatchEvent(
            new CustomEvent("tile-size-change", {
                detail: { size: { inline: col, block: row } },
                bubbles: true,
                composed: true,
            })
        );
    }

    private _renderTileSizeGrid() {
        const cells = [];
        for (let row = 1; row <= 4; row++) {
            for (let col = 1; col <= 4; col++) {
                const isSelected = this._selectedSize.inline >= col && this._selectedSize.block >= row;
                const isPreview =
                    this._previewSize && col <= this._previewSize.inline && row <= this._previewSize.block;

                cells.push(html`
                    <div
                        class="tile-size-cell ${isSelected ? "selected" : ""} ${isPreview ? "preview" : ""}"
                        @mouseenter=${() => this._handleGridHover(row, col)}
                        @click=${() => this._handleGridClick(row, col)}
                    ></div>
                `);
            }
        }

        // Get default size based on tile type
        const defaultSize = defaultDashboardConfig.tiles[this.tileType]?.size || { inline: 1, block: 1 };

        return html`
            <fieldset>
                <legend>Kachelgröße</legend>
                <div class="tile-size-grid" @mouseleave=${this._handleGridLeave}>${cells}</div>
                <input
                    type="hidden"
                    name="tileSize"
                    .value="${this._selectedSize.inline}x${this._selectedSize.block}"
                    .defaultValue="${defaultSize.inline}x${defaultSize.block}"
                />
            </fieldset>
        `;
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        // Clear all click handler timers
        this.clickHandlerTimers.forEach((timer) => clearTimeout(timer));
        this.clickHandlerTimers = [];

        // Remove document click listener
        if (this._documentClickListener) {
            document.removeEventListener("click", this._clickOutsideHandler);
            this._documentClickListener = false;
        }
    }

    private _documentClickListener = false;

    updated(changedProperties: Map<string, unknown>) {
        super.updated(changedProperties);

        if (changedProperties.has("settingsOpen")) {
            if (this.settingsOpen && !this._documentClickListener) {
                // Only add listener when settings are opened
                setTimeout(() => {
                    document.addEventListener("click", this._clickOutsideHandler);
                    this._documentClickListener = true;
                }, 0);
            } else if (!this.settingsOpen && this._documentClickListener) {
                // Remove listener when settings are closed
                document.removeEventListener("click", this._clickOutsideHandler);
                this._documentClickListener = false;
            }
        }
    }

    private _renderIcon() {
        if (!this.hasSettings) {
            return html`
                <div part="icon">
                    <ptc-ui-icon name="${this.icon}" part="tile-icon"></ptc-ui-icon>
                </div>
            `;
        }

        return html`
            <div part="icon">
                <ptc-ui-icon name="${this.icon}" part="tile-icon"></ptc-ui-icon>

                <label part="settings-toggle">
                    <ptc-ui-icon name="icon.action.apply-template" part="close-settings-icon"></ptc-ui-icon>
                    <ptc-ui-icon name="icon.feature.settings" part="open-settings-icon"></ptc-ui-icon>
                    <input
                        class="visually-hidden"
                        name="has-open-settings"
                        type="checkbox"
                        ?checked=${this.settingsOpen}
                        @click=${this.toggleSettings}
                    />
                </label>
            </div>
        `;
    }

    render() {
        if (typeof this.size === "number") {
            this.style.setProperty("--tile-size", this.size.toString());
        } else {
            this.style.setProperty("--tile-size-inline", this.size.inline.toString());
            this.style.setProperty("--tile-size-block", this.size.block.toString());
        }

        return html`
            <header part="header" ?hidden=${!this.label && !this.icon} data-has-settings=${this.hasSettings}>
                <div>
                    ${this.label} ${this.subLabel ? html`<div class="subtle smaller">${this.subLabel}</div>` : nothing}
                </div>
                <slot name="header"></slot>
                ${this.icon ? this._renderIcon() : nothing}
            </header>
            <div part="content" ?inert=${this.settingsOpen}>
                <slot></slot>
            </div>

            ${this.hasSettings
                ? html` <div part="settings" ?inert=${!this.settingsOpen}>
                      <div>
                          ${this.allowResize ? html` ${this._renderTileSizeGrid()} ` : nothing}
                          <slot name="settings"></slot>
                      </div>
                      <footer>
                          <slot name="settings-footer"></slot>
                          <button type="reset" id="resetSettingsButton" @click=${this._resetSettings}>
                              <ptc-ui-icon name="icon.action.reset"></ptc-ui-icon>
                          </button>
                          <ptc-ui-tooltip for="resetSettingsButton">Auf Standard zurücksetzen</ptc-ui-tooltip>
                      </footer>
                  </div>`
                : nothing}
        `;
    }
}
